// ============================================================================
// L'ÉTAT D'UNE SONDE, DIT À L'ÉCRAN — la couleur et le mot d'un statut, la part de relevés réussis.
// Partagé par la page Santé et la page Applications : deux écrans qui montrent la même sonde ne doivent pas en
// dire deux choses différentes.
// ============================================================================
import type { HealthMeasure, ServiceHealth } from './api';

/** Couleur du statut sonde → pastille/latence. C'est le SERVEUR qui range une sonde selon ses paliers (`HealthMeasure`). */
export const HEALTH_STATUS: Record<ServiceHealth['status'], { dot: string; fg: string; label: string }> = {
  ok: { dot: 'var(--chart-ok)', fg: 'var(--ok-fg)', label: 'opérationnel' },
  warn: { dot: 'var(--chart-wa)', fg: 'var(--wa-fg)', label: 'latence élevée' },
  high: { dot: 'var(--chart-da)', fg: 'var(--da-fg)', label: 'latence critique' },
  error: { dot: 'var(--chart-da)', fg: 'var(--da-fg)', label: 'en erreur' },
  down: { dot: 'var(--chart-da)', fg: 'var(--da-fg)', label: 'injoignable' },
  pending: { dot: 'var(--chart-ne)', fg: 'var(--ne-fg)', label: 'premier relevé…' },
};

/**
 * La part de relevés réussis, avec sa fenêtre.
 * ⚠️ `uptime` arrive DÉJÀ en pourcentage de la sonde (prober : ok/relevés ×100, ex. 99.9) : le multiplier encore
 *    afficherait « 9990.0 % ».
 * ⚠️ Une part de RELEVÉS n'est pas une DISPONIBILITÉ : la fenêtre l'accompagne partout, sinon « 100 % » se lit comme une
 *    promesse alors qu'il ne couvre que quelques minutes — un service tombé il y a une heure et revenu afficherait le
 *    même chiffre.
 */
export const fmtUptime = (u: number | null, minutes?: number) => (u === null ? '—' : `${u.toFixed(1)} %${minutes ? ` · ${minutes} min` : ''}`);

/**
 * LES TROIS PALIERS, DITS : la légende du tableau de bord et le sous-titre de Santé.
 * ⚠️ Lus dans la mesure du serveur, jamais écrits ici : « < 100 · 100–300 · ≥ 300 » doit rester la règle qui colore.
 */
export function latencyLegend(m: HealthMeasure): { key: 'ok' | 'warn' | 'high'; dot: string; label: string }[] {
  return [
    { key: 'ok', dot: HEALTH_STATUS.ok.dot, label: `< ${m.warnFromMs} ms` },
    { key: 'warn', dot: HEALTH_STATUS.warn.dot, label: `${m.warnFromMs}–${m.highFromMs}` },
    { key: 'high', dot: HEALTH_STATUS.high.dot, label: `≥ ${m.highFromMs}` },
  ];
}

/** Les zones de gravité du graphique d'historique — les MÊMES bornes que les tuiles, sinon l'écran se contredit. */
export function latencyZones(m: HealthMeasure): { de: number; a: number; fill: string }[] {
  return [
    { de: 0, a: m.warnFromMs, fill: HEALTH_STATUS.ok.dot },
    { de: m.warnFromMs, a: m.highFromMs, fill: HEALTH_STATUS.warn.dot },
    { de: m.highFromMs, a: Infinity, fill: HEALTH_STATUS.high.dot },
  ];
}
